import { useRef, useState } from 'react';
import { patientSortLabel } from '../../../../constants';
import styles from './PatientsSection.module.scss';
import { FilterList } from './components/FiltersList/FilterList';
import { SortOptions } from './components/SortOptions/SortOptions';
import cx from 'classnames';
import {
  BlockWrapperWithSpinner,
  Button,
  FilterChips,
  FiltersListModal,
  Icon,
  NoContent,
  PatientList,
  RolePermissionChecker,
  SearchInput,
  Text,
} from 'components';
import { useCreatePatient, useDevice, useOutsideClick } from 'hooks';
import { selectAppModals } from 'store/app/selectors';
import { appActions } from 'store/app/slice';
import {
  selectGetPatientsIsLoading,
  selectPatientListInfo,
  selectPatientPageFilters,
  selectPatients,
} from 'store/patients/selectors';
import { patientsActions } from 'store/patients/slice';
import { useDispatch, useSelector } from 'store/utils';
import { UserRole } from 'types';

export const PatientsSection = () => {
  const [showFilters, setShowFilters] = useState(false);
  const [showSort, setShowSort] = useState(false);
  const showFiltersButtonRef = useRef<HTMLDivElement | null>() as React.MutableRefObject<HTMLDivElement>;
  const showSortButtonRef = useRef<HTMLDivElement | null>() as React.MutableRefObject<HTMLDivElement>;
  const modals = useSelector(selectAppModals);
  const dispatch = useDispatch();
  const { isTablet } = useDevice();
  const filterRef = useOutsideClick<HTMLDivElement>((event) => {
    !showFiltersButtonRef?.current?.contains(event?.target as HTMLElement) && setShowFilters(false);
  });

  const patients = useSelector(selectPatients);
  const requestParams = useSelector(selectPatientPageFilters);
  const { clinicIds, doctorIds, isArchived, tasks, search, sort, treatmentPlanTypes, isTaskBlocked } = requestParams;
  const isLoadingPatients = useSelector(selectGetPatientsIsLoading);
  const info = useSelector(selectPatientListInfo);

  const { openModalHandler } = useCreatePatient();

  const hasFilters =
    clinicIds?.length ||
    doctorIds?.length ||
    isArchived ||
    tasks?.length ||
    treatmentPlanTypes?.length ||
    isTaskBlocked;

  const onOpenFiltersClick = () => {
    if (isTablet) {
      dispatch(appActions.setIsModalOpened({ name: 'patientFilters', isOpened: true }));
    } else {
      setShowFilters((isShown) => !isShown);
    }
  };

  const onOpenCreatePatientModalHandler = () => {
    openModalHandler();
  };

  const filtersAreEnabled =
    clinicIds?.length ||
    doctorIds?.length ||
    isArchived ||
    tasks?.length ||
    search ||
    sort ||
    treatmentPlanTypes?.length;

  const onCloseFilter = () => {
    dispatch(appActions.setIsModalOpened({ name: 'patientFilters', isOpened: false }));
  };

  const patientsContent =
    isLoadingPatients && !patients.length ? (
      <div className={styles.loaderContainer}>
        <BlockWrapperWithSpinner isLoading />
      </div>
    ) : patients.length ? (
      <PatientList
        patients={patients}
        className={styles.list}
        selectFilter={selectPatientPageFilters}
        selectInfo={selectPatientListInfo}
        setFilter={patientsActions.setPatientPageFilters}
        isLoading={isLoadingPatients}
      />
    ) : (
      <NoContent pageKey={filtersAreEnabled ? 'patientsListWithFilter' : 'patientsList'} />
    );

  return (
    <div className={styles.wrapper}>
      <div className={styles.patientTitleWrapper}>
        <Text type="h5">All patients</Text>
        <Text type="h5">{info?.totalElements}</Text>
        <RolePermissionChecker rolesAllowedToView={[UserRole.CLINIC]}>
          <div className={styles.createPatientButtonWrapper}>
            <Button onClick={onOpenCreatePatientModalHandler} className={styles.createPatientButton}>
              <Icon name="Plus" />
            </Button>
          </div>
        </RolePermissionChecker>
      </div>
      <SearchInput
        search={search}
        requestParams={requestParams}
        callback={dispatch}
        action={patientsActions.setPatientPageFilters}
      />
      <div className={styles.filterSortContainer}>
        <div className={styles.sortContainer}>
          <Text type="body2">Sort by</Text>
          <div className={styles.sortBlock}>
            <div
              className={cx(styles.sortWrapper, { [styles.sortShown]: showSort })}
              onClick={() => setShowSort((isShown) => !isShown)}
              ref={showSortButtonRef}>
              <Text type="subtitle2">{sort ? patientSortLabel[sort] : patientSortLabel.mostRecent}</Text>
              <Icon name="ArrowDown" />
            </div>
            <SortOptions
              setShowSort={setShowSort}
              open={showSort}
              requestParams={requestParams}
              refElement={showSortButtonRef}
            />
          </div>
        </div>
        <div className={styles.actionBlock}>
          <div className={styles.leftFiltersBlock} ref={showFiltersButtonRef} onClick={onOpenFiltersClick}>
            <Icon name="Settings" />
            <Text type="subtitle2">Filter</Text>
          </div>
          {showFilters && !isTablet ? (
            <div ref={filterRef} className={styles.filterListWrapper}>
              <FilterList onClose={() => setShowFilters(false)} />
            </div>
          ) : null}
        </div>
      </div>
      {!!hasFilters && (
        <div className={styles.filterChipWrapper}>
          <FilterChips
            filterSelector={selectPatientPageFilters}
            actionCreator={patientsActions.setPatientPageFilters}
          />
        </div>
      )}
      {patientsContent}
      {modals.patientFilters && isTablet && (
        <FiltersListModal onClose={onCloseFilter}>
          <FilterList />
        </FiltersListModal>
      )}
    </div>
  );
};
